import React, {
  createContext,
  ReactNode,
  useContext,
  useRef,
  useState,
} from "react";
import BranchListProvider from "./BranchListProvider";
import { SearchBranchList } from "../../../model/branch/BranchListModel";
import { useDispatch } from "react-redux";
import {
  addModal,
  ModalInterface,
  removeModal,
} from "../../../libraries/state_reducers/widget/ModalsReducer";
import ModalKeys from "../../../const/ModalKeys";
import { useSelector } from "react-redux";
import { RootState } from "../../../libraries/state_reducers/store";
import { routeName as routeNameHome } from "../../../screens/home/HomeScreen";
import { applyBranchSwitch } from "../../../libraries/state_reducers/home/HomeReducer";
import { routeName as routeNameSettingsChangeDefaultBranch } from "../../../screens/settings/change_default_branch/SettingsChangeDefaultBranchScreen";
import { applyChoosenDefaultBranch } from "../../../libraries/state_reducers/settings/SettingsChangeDefaultBranchReducer";
import { routeName as routeNameSignupVerification } from "../../../screens/onboarding/signup/account_verification/SignupAccountVerificationScreen";
import SignupAccountVerificationProvider from "../../onboarding/signup/account_verification/SignupAccountVerificationProvider";
import Loader from "../../../widget/common/ui/Loader";
import { routeName as routeNameLogin } from "../../../screens/onboarding/login/LoginScreen";
import LoginProvider from "../../onboarding/login/LoginProvider";
import { routeName as routeNameHomeGuest } from "../../../screens/home_guest_user/guest_home/HomeGuestUserScreen";
import HomeProvider from "../../home/HomeProvider";
import AppConfigService from "../../../service/app_config/AppConfigService";
import { ResponsiveDeviceType } from "../../../widget/layouts/ScreenMedia";
import UtilityHelper from "../../../service/utilities/UtilityHelper";
import CacheStorage from "../../../service/storage/CacheStorage";
import StorageKeys from "../../../const/StorageKeys";
import SessionStorage from "../../../service/storage/SessionStorage";
import { appRoutes } from "../../../routes/routes";
import { applyGuestBranchSwitch } from "../../../libraries/state_reducers/home_guest_user/HomeGuestUserReducer";
import { navigate } from "../../../service/navigation/Navigation";
import BranchSwitchSplashProvider from "../branch_switch_splash/BranchSwitchSplashProvider";
import AppTheme from "../../../service/app_config/AppTheme";

type BranchListContextType = {
  companyGroupType: string;
  branchList: Array<SearchBranchList> | null;
  searchValue: string;
  showSearch: boolean;
  updateShowSearch: () => void;
  handleOnChange: (name: string, value: string) => void;
  clearSearch: () => void;
  getBranchList: () => void;
  onClickBranch: (branch: SearchBranchList) => void;

  divisionTitle: string;
  switchDivision: () => void;
};

const BranchListContext = createContext<BranchListContextType | undefined>(
  undefined
);

export const BranchListContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const dispatch = useDispatch();
  const type: number = ResponsiveDeviceType();

  const modals = useSelector((state: RootState) => state.modals.list);

  const screenNameSelector = useSelector(
    (state: RootState) => state.branchList.screenName
  );

  const divisionTitle: string =
    SessionStorage.getValue(StorageKeys.division_title) || "";

  const companyGroupType: string =
    AppConfigService.get("company_group_type") ?? "0";

  const searchValueRef = useRef<string>("");
  const branchListResultRef = useRef<Array<SearchBranchList> | []>([]);

  const [branchList, setBranchList] = useState<Array<SearchBranchList> | null>(
    null
  );

  const [showSearch, setShowSearch] = useState<boolean>(type === 3);
  const [searchValue, setSearchValue] = useState<string>("");

  const onClickBranch = async (branch: SearchBranchList) => {
    CacheStorage.saveValue(StorageKeys.branch_id, branch.branch_id);

    if (screenNameSelector === routeNameHome) {
      dispatch(applyBranchSwitch({ switchBranchDetail: branch }));
      //
    } else if (screenNameSelector === routeNameSettingsChangeDefaultBranch) {
      dispatch(applyChoosenDefaultBranch({ branchDetail: branch }));
      //
    } else if (screenNameSelector === routeNameSignupVerification) {
      //
      Loader.modal();
      await HomeProvider.updateFollowStatusAPI(branch?.branch_id!);
      SignupAccountVerificationProvider.launchApp(branch?.branch_id!);
      /*  await SignupAccountVerificationProvider.updateDefaultBranch(
        branch?.branch_id!
      ); */
      Loader.modal(false);

      // THIS IS GUEST LOGIN
    } else if (
      [routeNameLogin, routeNameHomeGuest].includes(screenNameSelector)
    ) {
      console.log(`${screenNameSelector} === ${routeNameHomeGuest}`);

      LoginProvider.setUpGuestBranch(
        branch?.branch_id!,
        screenNameSelector === routeNameLogin
      );

      if (screenNameSelector === routeNameHomeGuest) {
        dispatch(applyGuestBranchSwitch({ switchBranchDetail: branch }));
      }
    }

    console.log(screenNameSelector, screenNameSelector);
    if (
      [
        routeNameLogin,
        routeNameHomeGuest,
        routeNameHome,
        routeNameSignupVerification,
      ].includes(screenNameSelector)
    ) {
      // REPLACE DIVISION / REVERESE
      CacheStorage.saveValue(
        StorageKeys.division_id,
        SessionStorage.getValue(StorageKeys.division_id)
      );
      CacheStorage.saveValue(
        StorageKeys.division_title,
        SessionStorage.getValue(StorageKeys.division_title)
      );

      if (
        [routeNameHome, routeNameSignupVerification].includes(
          screenNameSelector
        )
      ) {
        const branchColor: string | undefined = UtilityHelper.isNotEmpty(
          branch.branch_flash_colour ?? ""
        )
          ? branch.branch_flash_colour
          : CacheStorage.getValue(StorageKeys.company_default_theme) ||
            AppConfigService.getDefault("company_theme_color");

        const branchFlashLogo: string | undefined = UtilityHelper.isNotEmpty(
          branch.branch_flash_logo ?? ""
        )
          ? branch.branch_flash_logo
          : AppConfigService.getDefault("company_flash_logo");

        if (branchColor !== AppConfigService.get("company_theme_color")) {
          AppConfigService.update("company_theme_color", branchColor!);
        }

        if (branchFlashLogo !== AppConfigService.get("company_flash_logo")) {
          AppConfigService.update("company_flash_logo", branchFlashLogo!);
        }

        if (screenNameSelector === routeNameHome) {
          SessionStorage.saveValue(
            StorageKeys.branch_detail_boarding,
            JSON.stringify(branch)
          );

          navigate(appRoutes.routeNameBranchSwitchSplash, { replace: true });
        } else if (screenNameSelector === routeNameSignupVerification) {
          await BranchSwitchSplashProvider.setupBranchTheme(branch.branch_id!);
          // APPLY BRANCH THEME
          AppTheme.init();
        }
      }
    }

    dispatch(removeModal(ModalKeys.list_choose_branch));
    closeModalLogic();
  };

  const handleOnChange = (fieldName: string, fieldValue: string) => {
    fieldName = fieldName.toLowerCase();
    if (fieldName === `search-branch`) {
      setSearchValue(fieldValue);
      searchValueRef.current = fieldValue;

      const filtered: Array<SearchBranchList> | undefined =
        branchListResultRef.current?.filter((branch) =>
          Object.values(branch)
            .join(" ")
            .toLowerCase()
            .includes(searchValueRef.current.toLowerCase())
        );

      /* const filtered: Array<SearchBranchList> | undefined =
        branchListResultRef.current?.filter((branch) =>
          branch
            ?.title!.toLowerCase()
            .includes(searchValueRef.current.toLowerCase())
        ); */

      setBranchList(filtered!);
    }
  };

  const clearSearch = () => {
    if (UtilityHelper.isEmpty(searchValueRef.current) && type !== 3) {
      updateShowSearch();
    }

    setBranchList(branchListResultRef.current);
    setSearchValue("");
    searchValueRef.current = "";
  };

  const updateShowSearch = () => {
    setShowSearch(type === 3 ? true : !showSearch);
  };

  const getBranchList = async () => {
    const branchListModel = await BranchListProvider.getBranchListAPI(
      screenNameSelector === routeNameHome
    );
    if (branchListModel !== null) {
      const branchListResult: Array<SearchBranchList> | null =
        branchListModel?.result?.search_branch_list!;

      setBranchList(branchListResult);
      branchListResultRef.current = branchListResult;
    }
  };

  const switchDivision = () => {
    dispatch(removeModal(ModalKeys.list_choose_branch));
    closeModalLogic();

    dispatch(
      addModal({
        id: ModalKeys.list_choose_division,
        title: type === 3 ? "choose_division" : "divisions",
        screenMobileView: type !== 3,
        //size: "md",
      })
    );
  };

  const closeModalLogic = () => {
    const lastModal: ModalInterface = modals[modals.length - 1];
    if ((lastModal.screenMobileView ?? false) === true && type !== 3) {
      // window.history.back();
      window.history.replaceState(null, "", window.location.pathname);
    }
  };

  return (
    <BranchListContext.Provider
      value={{
        showSearch,
        companyGroupType,
        branchList,
        getBranchList,
        onClickBranch,
        searchValue,
        handleOnChange,
        clearSearch,
        updateShowSearch,

        divisionTitle,
        switchDivision,
      }}
    >
      {children}
    </BranchListContext.Provider>
  );
};

// IMPORT / USE CONTEXT ///////////////////////////////////////////////////////////////////
export const BranchListContextImport = () => {
  const context = useContext(BranchListContext);
  if (!context) {
    throw new Error(
      "BranchListContextImport must be used within a BranchListContextProvider"
    );
  }
  return context;
};
